import { createListenerMiddleware } from '@reduxjs/toolkit';

import syncCalendarCountriesOperation from './design/operation/calendarCountries/syncCalendarCountriesOperation';
import getFirstGridElement from './design/selector/getFirstGridElement';
import { addCalendarPublicEventsAction, setDesignDataFromVariantSwitchAction } from './design/slice';
import fitSheetToArtworkOperation from './gangSheet/operation/fitSheetToArtworkOperation';
import propagateDesignToGroupsOperation from './variants/operation/propagateDesignToGroupsOperation';
import checkPluginUse from './watchers/checkPluginUse';

import type { ThunkDispatch } from './hooks';
import type { RootState, ThunkArg } from './index';
import type { i18n } from 'i18next';

function createListener(i18n: i18n) {
  const listenerMiddleware = createListenerMiddleware<RootState, ThunkDispatch, ThunkArg>({ extra: { i18n } });

  listenerMiddleware.startListening({
    predicate: (action, currentState, previousState) =>
      !!action.type &&
      (currentState.design.designData !== previousState.design.designData ||
        currentState.design.globalPlugins !== previousState.design.globalPlugins),
    effect: (action, listenerApi) => {
      let state = listenerApi.getState();
      const { designData: originalDesignData } = listenerApi.getOriginalState().design;

      const isVariantSwitch = action.type === setDesignDataFromVariantSwitchAction.type;

      // Skip checkPluginUse when triggered by setInstalledPluginsAction — plugins were just computed and
      // are already correct, so re-running would be a no-op. We still continue past this block so that
      // the variant reflection below propagates the updated plugins into variationGroups and triggers
      // the postMessage to the host.
      if (action.type !== 'design/setInstalledPluginsAction' && state.design.designData) {
        checkPluginUse(
          state.design.designData,
          state.gallery.images,
          listenerApi.dispatch,
          state.plugins.list,
          state.design.globalPlugins,
          state.hostSettings.defaultPersonalizationPlugin,
        );
      }

      state = listenerApi.getState();
      const { designData } = state.design;

      // Holidays can arrive before the design, and the host may then replace the design with one
      // saved without them (the product wizard sets it twice), so re-apply them on either.
      const designLacksPublicHolidays =
        designData !== originalDesignData &&
        !!getFirstGridElement(state) &&
        !designData?.calendar?.events?.some(({ custom, merchant }) => !custom && !merchant);
      if (designData && state.gridDesigns.gridEvents.length && (!originalDesignData || designLacksPublicHolidays)) {
        listenerApi.dispatch(addCalendarPublicEventsAction(state.gridDesigns.gridEvents));
      }
      listenerApi.dispatch(syncCalendarCountriesOperation());
      // a rolling gang sheet's length follows its artwork
      listenerApi.dispatch(fitSheetToArtworkOperation());

      listenerApi.dispatch(propagateDesignToGroupsOperation(isVariantSwitch));
    },
  });

  // The host can send the gang sheet settings after the design has loaded; a rolling
  // sheet then needs its size recorded without waiting for the buyer's first edit.
  listenerMiddleware.startListening({
    predicate: (_action, currentState, previousState) =>
      currentState.hostSettings.gangSheet !== previousState.hostSettings.gangSheet,
    effect: (_action, listenerApi) => {
      listenerApi.dispatch(fitSheetToArtworkOperation());
    },
  });

  return listenerMiddleware;
}

export default createListener;
